import { LinkOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Space } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useEffect, useRef, useState } from 'react';
import { CustomNotification } from 'src/components/custom-notification';
import { Media, MediaFoldersNames } from 'src/lib/interface';
import VenueSelectDropDown from 'src/components/venue-selector';
import MediaUpload from '../media-upload';
import MultiDateSelector from '../multi-date';
import SelectTags from '../select-tags';
import { ImageOrEmbedded, MediaType } from 'src/lib/generated/_generated';

interface Props {
	sliderMedia?: Media;
	sliderMobileMedia?: Media;
	sliderVideoThumbnail?: string;
	sliderVideoLink?: string;
	sliderName?: string; // Story Name
	sliderSubtitle?: string; // Story Subtitle
	sliderButtonText?: string; // Button Text
	sliderLink?: string; //Optional - Link for story
	sliderPosition?: number | undefined; //Position of Story
	sliderVenueId?: string | undefined; // Venue ID
	sliderAppearIn?: string[];
	sliderTags?: string[]; //Appears in  Pages
	sliderDates?: string[]; //Selected Dates
	hideTagsAndDates?: boolean;
	canDelete?: boolean;
	onSave: (values: {
		dates: Date[];
		link: string;
		mediaId: string;
		venueId: string | undefined;
		position: number | null;
		name: string;
		subtitle: string;
		buttonText: string;
		tags: string[];
		mediaType: ImageOrEmbedded;
		videoLink: string | undefined;
		mobileMediaId: string | null;
	}) => void; //On Save Click
	onDelete?: React.MouseEventHandler<HTMLElement>; // On Delete Click
	inclSliderPosition?: boolean;
}
const SliderCard = (props: Props) => {
	const [reRender, setReRender] = useState(false);
	const selectedDays = useRef<Date[]>([]);
	const selectedHTags = useRef<string[]>([]);
	const uploadedMedia = useRef<Media | undefined>(undefined);
	const uploadedMobileMedia = useRef<Media | undefined>(undefined);
	const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>(
		props.sliderVenueId,
	);
	const [selectedMediaType, setSelectedMediaType] = useState<ImageOrEmbedded>(
		ImageOrEmbedded.Image,
	);

	const callParentSubmit = (data) => {
		if (!props.hideTagsAndDates && selectedDays.current.length === 0) {
			CustomNotification({
				pageName: 'Sliders',
				errorName: 'Date',
				notificationType: 'error',
			});
			return;
		}
		if (selectedMediaType === ImageOrEmbedded.Embedded && !data.videoLink) {
			CustomNotification({
				pageName: 'Sliders',
				errorName: 'Video Link',
				notificationType: 'error',
			});
			return;
		}

		if (!props.hideTagsAndDates && selectedHTags.current.length === 0) {
			CustomNotification({
				pageName: 'Sliders',
				errorName: 'Tag',
				notificationType: 'error',
			});
			return;
		}
		if (uploadedMedia.current === undefined) {
			CustomNotification({
				pageName: 'Sliders',
				errorName: 'Image',
				notificationType: 'error',
			});
			return;
		}
		props.onSave({
			dates: props.hideTagsAndDates ? [] : selectedDays.current,
			link: data.link,
			mediaId: uploadedMedia.current?.id || '-1',
			venueId: selectedVenueId,
			position: data.weight ? Number.parseInt(data.weight) : null,
			mediaType: selectedMediaType,
			name: data.name,
			subtitle: data.subtitle,
			buttonText: data.buttonText || 'Find out more',
			tags: props.hideTagsAndDates ? [] : selectedHTags.current,
			videoLink: data.videoLink,
			mobileMediaId: uploadedMobileMedia.current?.id || null,
		});
	};

	useEffect(() => {
		if (props.sliderMedia?.type === MediaType.Embedded) {
			uploadedMedia.current = props?.sliderMedia?.thumbnail || undefined;
			setSelectedMediaType(ImageOrEmbedded.Embedded);
		} else {
			uploadedMedia.current = props?.sliderMedia || undefined;
			setSelectedMediaType(ImageOrEmbedded.Image);
		}
		uploadedMobileMedia.current = props?.sliderMobileMedia || undefined;
		setReRender(!reRender);
	}, []);
	return (
		<Form
			onFinish={callParentSubmit}
			style={{
				width: '100%',
			}}
		>
			<Row style={{ width: '100%' }} gutter={16}>
				{/* canDelete would mean Edit Page is active */}
				{!props.canDelete && (
					<Col
						span={24}
						style={{
							margin: '8px 0',
						}}
					>
						<Row justify="end">
							<Space>
								<Button
									className={[
										'btn-main',
										selectedMediaType === ImageOrEmbedded.Image
											? 'active'
											: undefined,
									].join(' ')}
									onClick={() => {
										setSelectedMediaType(ImageOrEmbedded.Image);
									}}
								>
									Image
								</Button>
								<Button
									className={[
										'btn-main',
										selectedMediaType === ImageOrEmbedded.Embedded
											? 'active'
											: undefined,
									].join(' ')}
									onClick={() => {
										setSelectedMediaType(ImageOrEmbedded.Embedded);
									}}
								>
									Video
								</Button>
							</Space>
						</Row>
					</Col>
				)}

				{/* If Image Slider is True, show this */}
				<label className="input-fields-label">Main Image For Desktops, Laptops and Mobiles</label>
				{selectedMediaType === 'IMAGE' && (
					<Col span={24}>
						<MediaUpload
							folder={MediaFoldersNames.slider}
							onMediaUploaded={(media) => {
								uploadedMedia.current = media;
							}}
							media={uploadedMedia.current}
							accept="IMAGE"
							placeholderText="Drag and drop a slider image"
						/>
					</Col>
				)}

				{/* Optional Mobile Image */}
				<Col span={24}>
					<label className="input-fields-label">
						Mobile Image (optional — replaces main image on mobile with recommended size
						<span className={'size-note'}> 375px X 429px; aspect ratio of 9/16 </span>
						)
					</label>
					<MediaUpload
						folder={MediaFoldersNames.slider}
						onMediaUploaded={(media) => {
							uploadedMobileMedia.current = media;
							setReRender((r) => !r);
						}}
						media={uploadedMobileMedia.current}
						accept="IMAGE"
						placeholderText="Drag and drop a mobile-specific image (optional)"
					/>
				</Col>

				{/* Input Fields */}
				<Col span={24} md={selectedMediaType === 'IMAGE' ? 24 : 16}>
					<label className="input-fields-label">Name / Title</label>

					<Form.Item
						initialValue={props.sliderName}
						rules={[{ required: true }]}
						hasFeedback
						name="name"
						style={{
							margin: '8px 0',
						}}
					>
						<Input
							type="text"
							placeholder="Name / Title"
							className="input-fields"
						/>
					</Form.Item>
					<label className="input-fields-label">Subtitle</label>

					<Form.Item
						initialValue={props.sliderSubtitle}
						hasFeedback
						name="subtitle"
						style={{
							margin: '8px 0',
						}}
					>
						<Input
							type="text"
							placeholder="Subtitle"
							className="input-fields"
						/>
					</Form.Item>
					<label className="input-fields-label">Button Text</label>

					<Form.Item
						initialValue={props.sliderButtonText}
						hasFeedback
						name="buttonText"
						style={{
							margin: '8px 0',
						}}
					>
						<Input
							type="text"
							placeholder="By default: 'Find out more'"
							className="input-fields"
							maxLength={30}
						/>
					</Form.Item>
					<label className="input-fields-label">Link</label>

					<Form.Item
						initialValue={props.sliderLink}
						name="link"
						style={{
							margin: '8px 0',
						}}
					>
						<Input
							prefix={<LinkOutlined />}
							type="text"
							placeholder="Link"
							className="input-fields"
						/>
					</Form.Item>
					{props.inclSliderPosition && (
						<label className="input-fields-label">Position</label>
					)}

					{props.inclSliderPosition && (
					<Form.Item
						initialValue={props.sliderPosition}
						name="weight"
						rules={[
							{
								validator: (rule, value: string, back) => {
									if (value !== undefined) {
										if (value.length > 4) {
											back('Too large');
											return;
										}
										if (value !== '' && parseInt(value) < 1) {
											back('Minimum value is 1');
											return;
										}
									}
									back(undefined);
									return;
								},
							},
						]}
						style={{
							margin: '8px 0',
						}}
					>
						<Input
							type="number"
							min="1"
							placeholder="Position / Sequence No."
							className="input-fields"
						/>
					</Form.Item>
					)}

					<label className="input-fields-label">
						Venue ( This field is obligatory for the Event page )
					</label>

					<Form.Item
						style={{
							padding: '8px 0',
						}}
					>
						<VenueSelectDropDown
							value={selectedVenueId}
							onChange={(value: string) => setSelectedVenueId(value)}
						/>
					</Form.Item>

					{!props.hideTagsAndDates && (
						<>
							<label className="input-fields-label">Page Tags</label>

							<SelectTags
								initialValues={props.sliderTags || []}
								onSelectionChange={(tags) => {
									selectedHTags.current = tags;
								}}
								onInit={(tags) => (selectedHTags.current = tags)}
							/>

							<Text style={{ display: 'block', marginTop: '1em' }} strong={true}>
								Dates
							</Text>
							<Text style={{ display: 'block', fontSize: '12px', opacity: '0.50' }}>
								You can click on multiple dates to select the days to run the video
								for that duration
							</Text>
							<MultiDateSelector
								initialDates={props.sliderDates || []}
								onInit={(dates) => {
									selectedDays.current = dates;
								}}
								onChange={(dates) => (selectedDays.current = dates)}
							/>
						</>
					)}
					<hr style={{ opacity: '0.25' }} />

					<Row justify={'end'}>
						<Space size="small">
							<Button className="btn-main save" htmlType="submit">
								Save
							</Button>

							{props.canDelete ? (
								<Button className="btn-main delete" onClick={props.onDelete}>
									Delete
								</Button>
							) : (
								<Button
									className="btn-main"
									onClick={() => {
										window.history.back();
									}}
								>
									Cancel
								</Button>
							)}
						</Space>
					</Row>
				</Col>

				{/* Video Slider */}
				{selectedMediaType === ImageOrEmbedded.Embedded && (
					<Col
						span={24}
						md={8}
						style={{
							marginTop: '0.6em',
							height: '350px',
						}}
					>
						<MediaUpload
							folder={MediaFoldersNames.misc}
							onMediaUploaded={(media) => {
								uploadedMedia.current = media;
							}}
							media={uploadedMedia.current}
							accept="IMAGE"
							height={'350px'}
							placeholderText="Drag and drop a video thumbnail"
						/>
						<Form.Item
							initialValue={props.sliderMedia?.url}
							name="videoLink"
							style={{
								margin: '8px 0',
							}}
						>
							<Input
								prefix={<LinkOutlined />}
								type="text"
								placeholder="YouTube Video ID"
								className="input-fields"
								value={props.sliderVideoLink}
							/>
						</Form.Item>
					</Col>
				)}
			</Row>
		</Form>
	);
};

export default SliderCard;
